import React, { Fragment } from "react";
import { connect } from "react-redux";
import { Layout, Button, Row, Col, Card, Space, Switch, Typography, Divider, Tag, Grid, Tooltip, Modal, Table, Select, Input, notification } from 'antd';
import { SaveOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Header, CardCompanyBalance, FormatDate, FormatMoney, ColumnEmployee, BoxColor, SwitchLabel } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import { genericCheckout } from "services/solicitation-service";
import { getHolidays } from "services/banking-service";
import { DatePicker, Calendar } from "components";
import { SOLICITATION_SCHEDULE_PAYMENT_TYPE_DATA } from "utils/static-data";
import momentBR from "utils/moment-br";

const { Footer, Content } = Layout;
const { Text } = Typography;

const mapStateToProps = (state) => ({
    login: state.authReducer.loginReducer,
    balance: state.balanceReducer
});


const SolicitationRevisionScreen = React.forwardRef((props, ref) => {
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const location = useLocation();
    const { compact = false, onAfterSubmit, onSolicitationLines, balance, login } = props;
    const { data = {}, _data = [], users = [], benefits = []} = location.state || props;
    const [loading, setLoading] = React.useState(false);
    const [isDiscount, setIsDiscount] = React.useState(!compact);
    const [dates, setDates] = React.useState({ payment: momentBR(), expectation: momentBR().add(0, 'days'), approval: momentBR().add(0, 'days') })
    const [details, setDetails] = React.useState({ visible: false, title: "", data: null });
    const [year] = React.useState(momentBR().year());
    const [holidays, setHolidays] = React.useState([]);
    const [selectedBenefit] = React.useState(
        data.type === "credit" && data.body.data.benefit ?
            data.body.data.benefit :
            ((data.type === "charge" || data.type === "card") && data.body.solicitation.benefit ? data.body.solicitation.benefit : null)
    );
    const [incentive, setIncentive] = React.useState(false);
    const [finalize, setFinalize] = React.useState(false);
    const [schedule, setSchedule] = React.useState(false);
    const [paymentType, setPaymentType] = React.useState();
    const [saveSolicitation, setSaveSolicitation] = React.useState(false);
    const [solicitationName, setSolicitationName] = React.useState({ show: false, text: null, type: "text" });
    const [solicitationDescription, setSolicitationDescription] = React.useState({ show: false, text: null, type: "text" });
    const intentIdRef = React.useRef(crypto.randomUUID());

    React.useEffect(() => {
        if(!saveSolicitation && !schedule) {
            setPaymentType();
            setSolicitationName({ show: false, text: null, type: "text" });
            setSolicitationDescription({ show: false, text: null, type: "text" });
        }
    }, [saveSolicitation, schedule]);

    React.useEffect(() => {
        intentIdRef.current = crypto.randomUUID();
    }, [data.type, data.request]);


    const validateSolicitation = () => {
        const { available } = balance.company;
        const sumTotal = data.checkout.filter(check => check.label === "Valor Total");
        const sumTotalToFixed2 = sumTotal.length > 0 ? Number(sumTotal[0].value.toFixed(2)) : 0;
        const availableToFixed2 = Number(available.toFixed(2));
        const isInsufficientBalance = sumTotalToFixed2 > availableToFixed2 && data.type === "charge" && paymentType !== 'P';
        const { minScheduleValue, maxScheduleValue } = login.company;

        if(hasInvalidValue()) {
            notification.error({
                message: 'Atenção, uma ou mais cargas inválidas.',
                description: `O valor de cada benefício deve estar entre ${formatCurrency(minScheduleValue)} e ${formatCurrency(maxScheduleValue)}`
            });
            return false;
        }

        if(schedule && dates.approval <= momentBR().endOf('day')) {
            notification.error({
            message: 'Atenção',
            description: 'Data deve ser posterior ao dia atual!'
            });
            return false;
        }

        if (schedule && !paymentType) {
            notification.error({
            message: 'Atenção',
            description: 'Selecione um meio de pagamento!'
            });
            return false;
        }

        if (isInsufficientBalance) {
            notification.error({
            message: 'Saldo insuficiente',
            description: 'Valor do pedido excede o limite disponível'
            });
            return false;
        }

        if(saveSolicitation && !solicitationName.text) {
            notification.error({
            message: 'Atenção',
            description: 'O agendamento deve ter nome!'
            });
            return false;
        }

        return true;
    }

    const onSave = async () => {
        try {
            if (!validateSolicitation()) {
                return;
            }
            if (data.type === "charge") {
                data.body.solicitation.paymentAt = dates.payment.format('yyyy-MM-DDTHH:mm:ss');
                data.body.solicitation.approvalAt = dates.approval.format('yyyy-MM-DD');
                data.body.solicitation.discount = isDiscount;
                data.body.solicitation.paymentType = paymentType;
                data.body.solicitation.saveSolicitation = saveSolicitation;
                data.body.solicitation.solicitationName = solicitationName.text;
                data.body.solicitation.solicitationDescription = solicitationDescription.text;
            } else if (data.type !== "corporate") {
                data.body.data.paymentAt = dates.payment.format('yyyy-MM-DDTHH:mm:ss');
                data.body.data.approvalAt = dates.approval.format('yyyy-MM-DD');
                if (data.type === "card") {
                    data.body.solicitation.discount = isDiscount;
                }
            }
            const today = momentBR(new Date()).format('YYYY-MM-DD');
            const formattedDate = momentBR(dates.approval.format('YYYY-MM-DD')).format('DD/MM/YYYY');
            if(dates.approval.format('yyyy-MM-DD') !== today) {
                Modal.confirm({
                    title: `Confirma o pagamento para o dia ${formattedDate}?`,
                    content: "O pagamento só será realizado na data programada.",
                    onOk: async () => {
                        await onConfirm();
                    },
                    onCancel: () => {
                        return;
                    },
                    footer: (_, { OkBtn, CancelBtn }) => (
                        <>
                            <CancelBtn />
                            <OkBtn />
                        </>
                    ),
                });
            } else {
                await onConfirm();
            }
        } catch (ex) {
            console.log(ex)
        } finally {
            setLoading(false);
        }
    }

    const isValidValue = (value) => {
        const { minScheduleValue, maxScheduleValue } = login.company;
        return (value < minScheduleValue || value > maxScheduleValue) && (value !== 0) && schedule;
    }

    const hasInvalidValue = () => {
        if (!schedule) {
            return false;
        }
        const { minScheduleValue, maxScheduleValue } = login.company;
        const hasInvalidValue = data.body.solicitation.lines.some(
            ({ value }) => (value < minScheduleValue || value > maxScheduleValue) && value !== 0
        );
        return hasInvalidValue && schedule;
    }

    const onConfirm = async () => {
        try {
            const lines = onSolicitationLines ? await onSolicitationLines() : undefined;
            if (lines && lines.length === 0) {
                return;
            } else if (lines && lines.length > 0) {
                data.body.solicitation.lines = lines;
            }
            if(intentIdRef.current) {
                if (data.type === "charge") {
                    data.body.solicitation.intentId = intentIdRef.current;
                } else if (data.type === "credit") {
                    data.body.data.intentId = intentIdRef.current;
                } else if (data.type === "corporate") {
                    data.body.intentId = intentIdRef.current;
                }
            }
            const res = await genericCheckout(data.request, data.body, data.successMessage);
            if (res) {
                if (!onAfterSubmit) {
                    const processedSolicitation = Array.isArray(res)? res[0].id: res;
                    const preSolicitations = Array.isArray(res)? res.filter(solicitation => solicitation.id !== processedSolicitation): [];
                    if (data.type === "refund" || data.type === "charge" || data.type === "corporate") {
                        navigate(`/solicitation/${processedSolicitation}/report`, {state: { preSolicitations: preSolicitations}})
                    } else {
                        navigate("/solicitation")
                    }
                } else {
                    onAfterSubmit(true, res);
                }
            }
        } catch (ex) {
            console.log(ex)
        } finally {
            setLoading(false);
        }
    }

    const onContinue = async () => {
        setLoading(true);
        setFinalize(true);
    }

    const isHolidayDay = (date) => {
        return holidays.filter(d => d.date === date.format('YYYY-MM-DD')).length > 0
    }

    const getEndDate = (init, days) => {
        let date = momentBR(init); // use a clone

        //pedidos no sabado é como se fosse na segunda
        if (date.isoWeekday() === 6) {
            date = date.add(2, 'days');
        }
        //pedidos no feriado de sexta é como se fosse na segunda
        else if (date.isoWeekday() === 5 && isHolidayDay(date)) {
            date = date.add(3, 'days');
        }
        //pedidos no domingo e feriados //pedidos no feriado de sexta é como se fosse na segunda
        else if (date.isoWeekday() === 7 || isHolidayDay(date)) {
            date = date.add(1, 'days');
        }

        while (days > 0) {
            date = date.add(1, 'days');
            // desconsidera finais de semana e feriados
            if (date.isoWeekday() !== 6 && date.isoWeekday() !== 7 && !isHolidayDay(date)) {
                days -= 1;
            }
        }
        return date;
    }

    const calculateExpectationDates = (payment) => {
        let expectation = data.checkout.filter(x => x.total).length > 0 && data.checkout.filter(x => 
            (x.total && data.type !== "charge" && data.type !== "card") || (x.total && (data.type === "charge" || data.type === "card") && x.discount === isDiscount))[0].hasPayment ? getEndDate(payment, 0) : momentBR()
        setDates({ expectation: expectation, payment: payment, approval: momentBR(expectation) })
    }

    const dateCellRender = (e) => {
        const list = [];
        const value = e.format("YYYY-MM-DD");
        const holiday = holidays.filter(h => h.date === value)

        if (value === momentBR().format("YYYY-MM-DD")) {
            list.push(<Tag color={"default"}>HOJE</Tag>)
        }
        if (value === dates.payment.format("YYYY-MM-DD")) {
            list.push(<Tag color={"processing"}>PAGAMENTO</Tag>)
        }
        if (value === dates.approval.format("YYYY-MM-DD")) {
            list.push(<Tag color={"success"}>APROVAÇÃO</Tag>)
        }
        if (holiday.length > 0) {
            for (let h of holiday) {
                list.push(<Tag className="holiday" color={"error"}>{h.name}</Tag>)
            }
        }
        return list.map(x => (
            <div>
                {x}
            </div>
        ))
        // const listData = getListData(value);
        // return (
        //   <ul className="events">
        //     {listData.map(item => (
        //       <li key={item.content}>
        //         <Badge status={item.type} text={item.content} />
        //       </li>
        //     ))}
        //   </ul>
        // );
    }

    const formatCurrency = (amount) => {
        const formattedAmount = amount.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
        return formattedAmount;
    }

    const createLineCheckout = (x) => {
        const switchDiscount = !x.total && x.discount;
        return (
            <Row>
                {switchDiscount &&
                    <Col span={4} style={{ textAlign: "center" }}>
                        <Switch disabled={true} size="small" defaultChecked value={isDiscount} onChange={(value) => setIsDiscount(value)} />
                    </Col>
                }
                <Col span={6} offset={switchDiscount ? 0 : 4}>
                    <Text type="secondary">
                        {x.label}
                    </Text>
                </Col>
                {x.quantity !== undefined &&
                    <Col span={6} offset={2} style={{ textAlign: "right" }}>
                        <Text strong={true}>
                            {x.quantity}
                        </Text>
                    </Col>
                }
                {x.value !== undefined &&
                    <Col span={6} offset={2} style={{ textAlign: "right" }}>
                        <Text strong={true}>
                            <FormatMoney value={x.value} />
                        </Text>
                    </Col>
                }
                {x.valueName !== undefined &&
                    <Col span={6} offset={2} style={{ textAlign: "right" }}>
                        <Text strong={true}>{x.valueName}</Text>
                    </Col>
                }
                {x.details &&
                    <Col span={4} style={{ textAlign: "right" }}>
                        <Tooltip title="Detalhes">
                            <Button size="small" type="primary" shape="circle" icon={<EllipsisOutlined />}
                                onClick={() => {
                                    setDetails({ open: true, data: x.details, visible: true })
                                }}
                            />
                        </Tooltip>
                    </Col>
                }
            </Row>
        )
    }

    const getHoliday = async () => {
        const holidays = await getHolidays(year);
        setHolidays(holidays)
    }

    const init = () => {
        calculateExpectationDates(momentBR());
        if (data.type === "credit" || data.type === "refund") {
            setIsDiscount(false)
        }
        if (data.type === "credit" && data.body.data.benefit) {
            if (data.body.data.benefit < 0) {
                setIncentive(true)
            }
        }
        if (data.type === "charge" && data.body.solicitation.benefit) {
            if (data.body.solicitation.benefit < 0) {
                setIncentive(true)
            }
        }
    }

    const load = async () => {
        setLoading(true)
        await getHoliday()
        setLoading(false)
    }

    React.useEffect(() => {
        load()
        init()
    }, [])

    React.useEffect(() => {
        init()
    }, [holidays])

    React.useEffect(() => {
        if (finalize) {
            onSave();
            setFinalize(false);
        }
    }, [finalize])

    React.useEffect(() => {
        calculateExpectationDates(momentBR())
    }, [isDiscount])

    React.useImperativeHandle(ref, () => {
        return {
            submit() {
                onContinue();
            }
        };
    });

    return (
        <Layout className="alymente-layout">
            {!compact &&
                <Header title="Revisão">
                    <Row gutter={12}>
                        <Col xl={6} lg={8} md={12}>
                            <CardCompanyBalance benefit={selectedBenefit} useBenefit={login.company.benefitAccount ? true : false} />
                        </Col>
                        {/* <Col xl={6} lg={8} md={12}>
                            <CardCompanyBalance type={"discount"} benefit={selectedBenefit} useBenefit={login.company.benefitAccount ? true : false} />
                        </Col> */}
                        {data.type !== "corporate" &&
                            <Col xl={6} lg={8} md={12}>
                                <CardCompanyBalance type={"charge"} />
                            </Col>
                        }
                    </Row>
                </Header>
            }
            <Content>
                <Row gutter={[12, 16]} justify={data.type === "charge" ? "center" : "start"} align="stretch" style={{marginBottom: '20px'}}>
                    <Col xl={12} lg={24}>
                        <Card title="Revisão do Pedido">
                            <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                                {data.checkout.filter(x => !x.total).map(createLineCheckout)}
                                <Divider />
                                {data.checkout.filter(x => x.total && x.discount === isDiscount).map(createLineCheckout)}
                            </Space>
                        </Card>
                    </Col>
                    {/*data.type === "credit" &&
                        <Col xl={12} lg={24}>
                            <Card title="Revisão de Datas" style={{ height: screens.xl ? "100%" : "auto" }}>
                                <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                                    {
                                        (data.checkout.filter(x => x.total).length > 0 && data.checkout.filter(x =>
                                            (x.total && data.type !== "charge" && data.type !== "card") || (x.total && (data.type === "charge" || data.type === "card") && x.discount === isDiscount))[0].hasPayment) &&
                                        <Fragment>
                                            <Row align="middle">
                                                <Col xxl={8} xl={10} lg={12}>
                                                    <Text type="secondary">
                                                        Planejo pagar no dia
                                                    </Text>
                                                </Col>
                                                <Col xxl={8} xl={10} lg={12}>
                                                    <DatePicker format={(v) => momentBR(v).format('DD/MM/YYYY')} value={dates.payment} clearIcon={false}
                                                        onChange={(value) => calculateExpectationDates(value)}
                                                    />
                                                </Col>
                                            </Row>
                                            {
                                                    <Row align="middle">
                                                        <Col xxl={8} xl={10} lg={12}>
                                                            <Text type="secondary">
                                                                Expectativa de Aprovação
                                                            </Text>
                                                        </Col>
                                                        <Col xxl={8} xl={10} lg={12} style={{ textAlign: "start", marginLeft: 10 }}>
                                                            <Text strong={true}>
                                                                <FormatDate value={dates.expectation} />
                                                            </Text>
                                                        </Col>
                                                    </Row>
                                            }
                                        </Fragment>
                                    }
                                    {
                                            <Row align="middle">
                                                <Col xxl={8} xl={10} lg={12}>
                                                    <Text type="secondary">
                                                        Apenas aprovar no dia
                                                    </Text>
                                                </Col>
                                                <Col xxl={8} xl={10} lg={12}>
                                                    <DatePicker format={(v) => momentBR(v).format('DD/MM/YYYY')} value={dates.approval} clearIcon={false}
                                                        onChange={(value) => setDates((prev) => ({ ...prev, approval: value }))}
                                                    />
                                                </Col>
                                            </Row>
                                    }
                                </Space>
                            </Card>
                        </Col>
										*/
                    }
                    {data.type === "charge" &&
                        <Col xl={12} lg={24}>
                            <Card title="Revisão de Datas" style={{ height: screens.xl ? "100%" : "auto" }}>
                                <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                                    <Fragment>
                                        {login.company?.schedule &&
                                            <Row align="middle">
                                                <Col xxl={8} xl={10} lg={12}>
                                                    <Text type="secondary">
                                                        Agendar pagamento?
                                                    </Text>
                                                </Col>
                                                <Col xxl={8} xl={10} lg={12}>
                                                    <SwitchLabel onChange={(value) => {
                                                        if(!value) {
                                                            dates.approval = momentBR().add(0, 'days');
                                                        }
                                                        setSchedule(value);
                                                    }} label="" value={schedule}/>
                                                </Col>
                                            </Row>
                                        }
                                        {schedule && login.company?.schedule &&
                                            <Fragment>
                                                <Row align="middle">
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Text type="secondary">
                                                            Apenas aprovar no dia
                                                        </Text>
                                                    </Col>
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <DatePicker
                                                            disabledDate={(current) => {
                                                                if (!current) return false;
                                                                const isPast = current.isBefore(momentBR(), 'day');
                                                                const isWeekend = current.day() === 0 || current.day() === 6;
                                                                return isPast || isWeekend;
                                                            }}
                                                            format={(v) => momentBR(v).format('DD/MM/YYYY')} value={dates.approval} clearIcon={false}
                                                            onChange={(value) => setDates((prev) => ({ ...prev, approval: value }))}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row align="middle">
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Text type="secondary">
                                                            Pagamento
                                                        </Text>
                                                    </Col>
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Select onChange={(value => setPaymentType(value))} placeholder="Selecione o meio de pagamento">
                                                            {SOLICITATION_SCHEDULE_PAYMENT_TYPE_DATA.map(x => <Select.Option value={x.code}>{x.name}</Select.Option>)}
                                                        </Select>
                                                    </Col>
                                                </Row>
                                                <Row align="middle">
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Text Alert type="secondary">
                                                            {saveSolicitation && <span style={{ color: "red" }}>* </span>}Nome
                                                        </Text>
                                                    </Col>
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Input maxLength={20} value={solicitationName.text}
                                                        onChange={(e) => setSolicitationName((prev) => ({ ...prev, text: e.target.value }))}></Input>
                                                    </Col>
                                                </Row>
                                                <Row align="middle">
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Text type="secondary">
                                                            Descrição
                                                        </Text>
                                                    </Col>
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Input.TextArea rows={2} maxLength={100} value={solicitationDescription.text}
                                                        onChange={(e) => setSolicitationDescription((prev) => ({ ...prev, text: e.target.value }))}></Input.TextArea>
                                                    </Col>
                                                </Row>
                                            </Fragment>
                                        }
                                        <Row align="middle">
                                            <Col xxl={8} xl={10} lg={12}>
                                                <Text type="secondary">
                                                    Salvar Pedido
                                                </Text>
                                            </Col>
                                            <Col xxl={8} xl={10} lg={12}>
                                                <SwitchLabel onChange={(value) => setSaveSolicitation(value)} label="" value={saveSolicitation}/>
                                            </Col>
                                        </Row>
                                        {saveSolicitation && !schedule && !login.company?.schedule &&
                                            <Fragment>
                                                <Row align="middle">
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Text type="secondary">
                                                            {saveSolicitation && <span style={{ color: "red" }}>* </span>}Nome
                                                        </Text>
                                                    </Col>
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Input maxLength={20} value={solicitationName.text}
                                                        onChange={(e) => setSolicitationName((prev) => ({ ...prev, text: e.target.value }))}></Input>
                                                    </Col>
                                                </Row>
                                                <Row align="middle">
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Text type="secondary">
                                                            Descrição
                                                        </Text>
                                                    </Col>
                                                    <Col xxl={8} xl={10} lg={12}>
                                                        <Input.TextArea rows={2} maxLength={100} value={solicitationDescription.text}
                                                        onChange={(e) => setSolicitationDescription((prev) => ({ ...prev, text: e.target.value }))}></Input.TextArea>
                                                    </Col>
                                                </Row>
                                            </Fragment>
                                        }
                                    </Fragment>
                                </Space>
                            </Card>
                        </Col>
                    }
                </Row>
                <Row gutter={12} style={{marginBottom: '20px'}}>
                    <Col xl={24}>
                        {
                            users.length > 0 && benefits.length > 0 ? ( <>
																<Text> Qtd. de Participantes: {users.length} </Text>
                                <Table
                                    bordered
                                    pagination={false}
                                    dataSource={users}
                                    scroll={{
                                        x: 'max-content',
                                        y: 450,
                                    }}
                                    columns={[
                                        {
                                            fixed: "left",
                                            key: "name",
                                            width: 300,
                                            dataIndex: "employee",
                                            title: "Participante",
                                            render: (value) => (
                                                <ColumnEmployee
                                                    name={value.name}
                                                    email={value.email}
                                                    cpf={value.cpf}
																										businessName={value?.businessName}
                                                    cnpj={value?.cnpj}
                                                />
                                            )
                                        },
                                        ...Array.from(benefits).map(benefit => ({
                                            align: "right",
                                            width: 310,
                                            key: benefit.benefit,
                                            dataIndex: benefit.key,
                                            title: () => (
                                                <Row width="100%" style={{ alignItems: "center", justifyContent: "center" }}>
                                                    <Col><BoxColor color={benefit.color} /></Col>
                                                    <Col style={{ marginLeft: 10 }}>
                                                        <Space>{benefit.name}</Space>
                                                    </Col>
                                                </Row>
                                            ),
                                            render: (value) => <FormatMoney danger={
                                                isValidValue(value.value)
                                            } value={value.value} />
                                        }))
                                    ]}
                                /> </>) :
                                <Card>
                                    <Calendar defaultValue={null} dateCellRender={dateCellRender} disabledDate={(e) => {
                                        const value = e.format("YYYY-MM-DD");
                                        const holiday = holidays.filter(h => h.date === value)

                                        if (holiday.length > 0 || e.isoWeekday() === 6 || e.isoWeekday() === 7) {
                                            return true
                                        }
                                        return false
                                    }} />
                                </Card>
                        }
                    </Col>
                </Row>
            </Content>
            {!compact &&
                <Footer style={{display:"flex",justifyContent:"space-between", alignItems:"center"}}>
                    <Button
                        className="btn-save"
                        type="primary"
                        onClick={() => {
                            Modal.confirm({
                                title: "Deseja Voltar?",
                                content: "Este pedido será desfeito.",
                                onOk: () => navigate(-1),
                                footer: (_, { OkBtn, CancelBtn }) => (
                                    <>
                                        <CancelBtn />
                                        <OkBtn />
                                    </>
                                ),
                            });
                        }}>
                        Voltar
                    </Button>
                    {hasInvalidValue() && <Button
                        className="btn-save"
                        type="primary"
                        onClick={() => {
                            Modal.confirm({
                            title: "Deseja Revisar pedido?",
                            content: "",
                            onOk: () => {
                                navigate("/solicitation/charge/incentive/save", {
                                state: { revisionData: [..._data] },
                                replace: true
                                });
                            },
                            footer: (_, { OkBtn, CancelBtn }) => (
                                <>
                                <CancelBtn />
                                <OkBtn />
                                </>
                            ),
                            });
                        }}>
                        Revisar pedido
                        </Button>}
                    <Button disabled={loading || finalize} loading={loading} className="btn-save" type="primary" icon={<SaveOutlined />} onClick={onContinue}>Finalizar</Button>
                </Footer>
            }
            <Modal width={1200}
                title={
                    <React.Fragment>
                        <Typography>{details.data ? details.data.title : ""}</Typography>
                        <Typography.Text type="secondary">{details.data ? details.data.subtitle : ""}</Typography.Text>
                    </React.Fragment>
                }
                open={details.visible} onCancel={() => { setDetails({ visible: false }) }}
                footer={[
                    <Button type="primary" key="back" onClick={() => { setDetails({ visible: false }) }}>
                        Ok
                    </Button>
                ]}
            >
                <Table bordered scroll={{ y: 450 }}
                    pagination={false}
                    dataSource={details.data ? details.data.data : []}
                    columns={details.data ?
                        details.data.columns
                            .map(x => ({
                                ...x,
                                ...(
                                    x.details ? { render: (value) => x.details.type === "money" ? <FormatMoney value={value} /> : <FormatDate value={value} format={x.details.format} /> } : {}
                                )

                            }))
                        : []} />
            </Modal>
        </Layout >
    )
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(SolicitationRevisionScreen);
